import { useEffect } from 'react'
import { useLocalStorage } from "@uidotdev/usehooks"
import { useRollbar } from '@rollbar/react'
import isEqual from 'lodash/isEqual'

import useAuthentication from './useAuthentication';

const CACHE_MINUTES = 1

const useQualityContents = () => {
    const rollbar = useRollbar()
    const { currentUser } = useAuthentication();
    
    const [lastUpdated, setLastUpdated] = useLocalStorage('quality_contents_last_updated', 0)
    const [qmsDocuments, setQmsDocuments ] = useLocalStorage('quality_contents_qms_documents', [])
    const [qaDocuments, setQaDocuments ] = useLocalStorage('quality_contents_qa_documents', [])
    const [qaRecords, setQaRecords ] = useLocalStorage('quality_contents_qa_records', [])

    useEffect(() => {
        const shouldClearCache = lastUpdated + (1000 * 60 * CACHE_MINUTES) < Date.now();

        if (shouldClearCache) {
            reloadQualityContents();
        }
    }, [])

    const reloadQualityContents = () => {
        //rollbar?.debug?.("useQualityContents reloadQualityContents")
        setLastUpdated(Date.now())
        _reloadQmsDocuments()
        _reloadQaDocuments()
        _reloadQaRecords()
    }

    const _processQmsDocument = (row) => {
        const item = {}
        item.id = row.id
        item.reference = row.reference
        item.title = row.title
        item.owner_user_id = row.owner_user_id
        item.created_at = row.created_at
        item.final_version_file_path = row.final_version_file_path
        item.area_name = row.area.name
        item.iso_section_number = row.iso_section_number
        item.qms_document_type_name = row.qms_document_type.name
        item.qms_document_status_name = row.qms_document_status.name
        return item
    }
    
    const _processQaDocument = (row) => {
        const item = {}
        item.id = row.id
        item.reference = row.reference
        item.title = row.title
        item.owner_user_id = row.owner_user_id
        item.created_at = row.created_at
        item.final_version_file_path = row.final_version_file_path
        item.area_name = row.area.name
        item.iso_section_number = row.iso_section_number
        item.qa_document_type_name = row.qa_document_type.name
        item.qa_document_status_name = row.qa_document_status.name
        return item
    }

    const _processQaRecord = (row) => {
        const item = {}
        item.id = row.id
        item.reference = row.reference
        item.title = row.title
        item.owner_user_id = row.owner_user_id
        item.created_at = row.created_at
        item.final_version_file_path = row.final_version_file_path
        item.qa_record_type_name = row.qa_record_type.name
        item.qa_record_status_name = row.qa_record_status.name
        return item
    }

    const _reload = (objType, list, setCb, processCb) => {
        fetch('/v0/' + objType, { credentials: 'same-origin' })
            .then(function(response) {
                if (!response.ok) {
                    return response.text().then((bodyText) => {
                        const error = new Error(`HTTP error ${response.status}`);
                        error.response = response;
                        error.body = bodyText;
                        throw error;
                    })
                }
                return response.json()
            })
            .then(function(json) {
                if( json.error && json.error === 'ENOACCESS' ) {
                    setCb([])
                    return
                }
                const data = json.results.map((row) => {
                    return processCb(row)
                })
                if( ! isEqual(data, list) ) {
                    setCb(data)
                }
            })
            .catch(error => {
                if (error?.response?.status === 403) {
                    // Just ignore this, they don't have access
                    return
                }
                rollbar?.error?.("useQualityContents reload error", error, {
                    objType: objType,
                    currentUserId: currentUser?.id,
                    status: error?.response?.status,
                    body: error?.body,
                })
            })
    }
    
    const _reloadQmsDocuments = () => _reload('qms_documents', qmsDocuments, setQmsDocuments, _processQmsDocument)
    const _reloadQaDocuments = () => _reload('qa_documents', qaDocuments, setQaDocuments, _processQaDocument)
    const _reloadQaRecords = () => _reload('qa_records', qaRecords, setQaRecords, _processQaRecord)
    
    const clearQualityContents = () => {
        setQmsDocuments([])
        setQaDocuments([])
        setQaRecords([])
        setLastUpdated(0)
    }

    ///
    
    const _updateOrInsert = (list, data) => {
        const index = list.findIndex(item => item.id === data.id)
        if ( index >= 0 ) {
            list[index] = data
        } else {
            list.push(data)
        }
        return list
    }

    const _update = (input, process, list, setCb) => {
        console.log("_update", input)
        const data = process(input)
        const updated = _updateOrInsert(list, data)
        setCb(updated)
    }

    const _delete = (id, list, setCb) => {
        const updated = list.filter(item => item.id !== id)
        setCb(updated)
    }

    const updateQmsDocument = (item) => _update(item, _processQmsDocument, qmsDocuments, setQmsDocuments)
    const deleteQmsDocument = (id)   => _delete(id, qmsDocuments, setQmsDocuments)
    const updateQaDocument  = (item) => _update(item, _processQaDocument, qaDocuments, setQaDocuments)
    const deleteQaDocument  = (id)   => _delete(id, qaDocuments, setQaDocuments)
    const updateQaRecord    = (item) => _update(item, _processQaRecord, qaRecords, setQaRecords)
    const deleteQaRecord    = (id)   => _delete(id, qaRecords, setQaRecords)
    
    // When doing actions that might affect other items (for example changing Status on a Doc can change the Statuses of several other docs)
    // we do need a full reload of the contents
    const forceReloadQmsDocuments = async () => {
        await _reloadQmsDocuments();
    }
    
    const forceReloadQaDocuments = async () => {
        await _reloadQaDocuments();
    }
    
    return {
        clearQualityContents,
        reloadQualityContents,
        
        qmsDocuments,
        qaDocuments,
        qaRecords,

        forceReloadQmsDocuments,
        forceReloadQaDocuments,
        
        // These are all for updating localStorage
        updateQmsDocument,
        deleteQmsDocument,
        updateQaDocument,
        deleteQaDocument,
        updateQaRecord,
        deleteQaRecord,
    }
}

export default useQualityContents
